<template>
    <div
        class="box box-primary dashboard-report"
        :class="{ 'is-loading': isLoading }"
    >
        <div class="box-header with-border">
            <h3 class="box-title">{{ $t('report') }}</h3>

            <img
                v-show="isLoading"
                class="table-row-preloader ml10"
                rel="preload"
                src="/img/table-row-preloader.gif"
            />

            <div class="box-tools pull-right">
                <button
                    class="btn btn-box-tool"
                    :title="$t('collapse')"
                    type="button"
                    @click="isCollapse = !isCollapse"
                >
                    <i
                        class="fa"
                        :class="{
                            'fa-plus': isCollapse,
                            'fa-minus': !isCollapse,
                        }"
                    />
                </button>
            </div>
        </div>

        <template v-if="!isLoading">
            <vue-slide-up-down
                :active="!isCollapse"
            >
                <div v-if="isReportItems">
                    <div
                        v-if="!isLoading"
                        class="box-body"
                    >
                        <div class="row">
                            <div class="col-md-4 col-xs-12">
                                <template v-if="domains">
                                    <strong>{{ $t('domains') }}:</strong>

                                    <p>
                                        <BadgeTotal
                                            :value="'$' + domains.cost || 0"
                                            color="light-blue"
                                            :title="$t('cost')"
                                        />

                                        <BadgeTotal
                                            :value="domains.count || 0"
                                            color="aqua-active"
                                            :title="$t('count')"
                                        />
                                    </p>
                                </template>
                            </div>

                            <div class="col-md-4 col-xs-12">
                                <template v-if="domains">
                                    <strong>{{ $t('woMessages') }}:</strong>

                                    <p>
                                        <span
                                            class="label switcher"
                                            :class="{
                                                'bg-gray': onlyWithMessages,
                                                'bg-light-blue': !onlyWithMessages,
                                            }"
                                            @click="filterByMessages"
                                        >
                                            {{ onlyWithMessages ? $t('disabled') : $t('enabled') }}
                                        </span>
                                    </p>
                                </template>
                            </div>

                            <div class="col-md-4 col-xs-12">
                                <button
                                    class="btn btn-sm btn-info pull-right"
                                    type="button"
                                    @click="changeAdditionalFieldsVisibility()"
                                >
                                    {{ $t('additionalFields') }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="box-body table-responsive no-padding">
                        <table class="table table-hover dynamic-table dashboard-table">
                            <thead class="bg-gray-light">
                                <tr>
                                    <th>
                                        <SortBy
                                            :sort="getSort('id')"
                                            name="id"
                                            title="ID"
                                        />
                                    </th>

                                    <th>{{ typeReport }}</th>

                                    <th
                                        v-for="(value, idx) in additionalRelationTitles"
                                        :key="idx"
                                    >
                                        {{ value }}
                                    </th>

                                    <th>
                                        <SortBy
                                            title="Total"
                                            :sort="getSort('messages_count_number')"
                                            name="messages_count_number"
                                        />
                                    </th>

                                    <th>
                                        <SortBy
                                            title="Sent"
                                            :sort="getSort('messages_sent_number')"
                                            name="messages_sent_number"
                                        />
                                    </th>

                                    <th>
                                        <SortBy
                                            title="Clicks"
                                            :sort="getSort('clicks_count_number')"
                                            name="clicks_count_number"
                                        />
                                    </th>

                                    <th>
                                        <SortBy
                                            title="Revenue"
                                            :sort="getSort('revenue_count_number')"
                                            name="revenue_count_number"
                                        />
                                    </th>

                                    <th>
                                        <SortBy
                                            title="pCPM"
                                            :sort="getSort('pcpm_number')"
                                            name="pcpm_number"
                                        />
                                        /
                                        <SortBy
                                            title="eCPM"
                                            :sort="getSort('ecpm_number')"
                                            name="ecpm_number"
                                        />
                                        /
                                        <SortBy
                                            title="EPC"
                                            :sort="getSort('epc_number')"
                                            name="epc_number"
                                        />
                                    </th>

                                    <th>
                                        <SortBy
                                            title="Cost"
                                            :sort="getSort('cost_number')"
                                            name="cost_number"
                                        />
                                    </th>

                                    <th>
                                        <SortBy
                                            title="Profit"
                                            :sort="getSort('profit_amount_number')"
                                            name="profit_amount_number"
                                        />
                                    </th>

                                    <th>
                                        <SortBy
                                            title="N pCPM"
                                            :sort="getSort('pcpm_negative')"
                                            name="pcpm_negative"
                                        />
                                    </th>

                                    <th v-if="isAdditionalFieldsHidden">
                                        <SortBy
                                            title="Undelivered"
                                            :sort="getSort('messages_undelivered_number')"
                                            name="messages_undelivered_number"
                                        />
                                    </th>

                                    <th v-if="isAdditionalFieldsHidden">
                                        <SortBy
                                            title="Failed"
                                            :sort="getSort('messages_failed_number')"
                                            name="messages_failed_number"
                                        />
                                    </th>

                                    <th v-if="isAdditionalFieldsHidden">
                                        <SortBy
                                            title="Blacklisted"
                                            :sort="getSort('messages_blacklisted_number')"
                                            name="messages_blacklisted_number"
                                        />
                                    </th>

                                    <th v-if="isAdditionalFieldsHidden">
                                        <SortBy
                                            title="Unsubscribed"
                                            :sort="getSort('messages_unsubscribed_number')"
                                            name="messages_unsubscribed_number"
                                        />
                                    </th>

                                    <th v-if="isAdditionalFieldsHidden">
                                        <SortBy
                                            title="Two Way Replied"
                                            :sort="getSort('messages_2way_replied_number')"
                                            name="messages_2way_replied_number"
                                        />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-if="items.length > 5"
                                    class="without-color"
                                >
                                    <th :colspan="colspanTotal">Total</th>
                                    <th>
                                        <span title="Total">{{ report.totals.messages_count }}</span>
                                    </th>
                                    <th>
                                        <span title="Sent">{{ report.totals.messages_sent_percent }}% ({{ report.totals.messages_sent }})</span>
                                    </th>
                                    <th>{{ report.totals.clicks_percent }}% ({{ report.totals.clicks_count }})</th>
                                    <th>${{ report.totals.revenue_amount }} ({{ report.totals.revenue_count }})</th>
                                    <th>${{ report.totals.pcpm }} / ${{ report.totals.ecpm }} / ${{ report.totals.epc }}</th>
                                    <th>${{ report.totals.cost }}</th>
                                    <th>${{ report.totals.profit_amount }}</th>
                                    <th>
                                        <ReverseYesNo :value="report.totals.pcpm_negative" />
                                    </th>
                                    <th v-if="isAdditionalFieldsHidden">
                                        <span title="Undelivered"
                                            >{{ report.totals.messages_undelivered_percent }}% ({{ report.totals.messages_undelivered }})</span
                                        >
                                    </th>
                                    <th v-if="isAdditionalFieldsHidden">
                                        <span title="Failed"
                                            >{{ report.totals.messages_failed_percent }}% ({{ report.totals.messages_failed }})</span
                                        >
                                    </th>
                                    <th v-if="isAdditionalFieldsHidden">
                                        <span title="Blacklisted"
                                            >{{ report.totals.messages_blacklisted_percent }}% ({{ report.totals.messages_blacklisted }})</span
                                        >
                                    </th>
                                    <th v-if="isAdditionalFieldsHidden">
                                        <span title="Unsubscribed"
                                            >{{ report.totals.messages_unsubscribed_percent }}% ({{
                                                report.totals.messages_unsubscribed
                                            }})</span
                                        >
                                    </th>
                                    <th v-if="isAdditionalFieldsHidden">
                                        <span title="2WayReplied"
                                            >{{ report.totals.messages_2way_replied_percent }}% ({{
                                                report.totals.messages_2way_replied
                                            }})</span
                                        >
                                    </th>
                                </tr>

                                <template v-for="(value, idx) in items">
                                    <tr
                                        v-if="!onlyWithMessages || value.messages_count !== '0'"
                                        :key="idx"
                                    >
                                        <td>
                                            <span v-if="value.entity">{{ value.entity.id }} </span>
                                            <span v-else>-</span>
                                        </td>
                                        <td>
                                            <span v-if="value.entity && value.entity.route">
                                                <a :href="value.entity.route">{{ value.entity.name }}</a>
                                            </span>
                                            <span v-else-if="value.entity">{{ value.entity.name }}</span>
                                            <span v-else>Unknown</span>
                                        </td>
                                        <td
                                            v-for="(valueRelation, idxRelation) in value.additional_relations"
                                            :key="idxRelation"
                                        >
                                            <span v-if="valueRelation.relation && valueRelation.relation.route">
                                                <a :href="valueRelation.relation.route">{{ valueRelation.relation.name }}</a>
                                            </span>
                                            <span v-else-if="valueRelation.relation">{{ valueRelation.relation.name }}</span>
                                            <span v-else>Unknown</span>
                                        </td>
                                        <td>
                                            <span
                                                class="label bg-gray"
                                                title="Total Messages"
                                            >
                                                {{ value.messages_count }}
                                            </span>
                                        </td>
                                        <td>
                                            <span
                                                class="label bg-green"
                                                title="Sent Messages"
                                            >
                                                {{ value.messages_sent_percent }}%
                                            </span>

                                            <span
                                                class="label bg-green"
                                                title="Sent Messages"
                                            >
                                                {{ value.messages_sent }}
                                            </span>
                                        </td>

                                        <td>
                                            <span
                                                class="label bg-light-blue"
                                                title="Clicks"
                                            >
                                                {{ value.clicks_percent }}%
                                            </span>

                                            <span
                                                class="label bg-aqua-active"
                                                title="Clicks"
                                            >
                                                {{ value.clicks_count }}
                                            </span>
                                        </td>
                                        <td>
                                            <span
                                                class="label bg-light-blue"
                                                title="Revenue"
                                            >
                                                ${{ value.revenue_amount }}
                                            </span>

                                            <span
                                                class="label bg-aqua-active"
                                                title="Revenue"
                                            >
                                                {{ value.revenue_count }}
                                            </span>
                                        </td>
                                        <td>
                                            <BadgeTotal
                                                :convert-negative="true"
                                                :value="value.pcpm"
                                                color="green"
                                                symbol="$"
                                                title="pCPM"
                                            />

                                            <BadgeTotal
                                                :value="'$' + value.ecpm"
                                                color="light-blue"
                                                title="eCPM"
                                            />

                                            <BadgeTotal
                                                :value="'$' + value.epc"
                                                color="aqua-active"
                                                title="EPC"
                                            />
                                        </td>

                                        <td>
                                            <span
                                                class="label bg-light-blue"
                                                title="Cost"
                                            >
                                                ${{ value.cost }}
                                            </span>
                                        </td>

                                        <td>
                                            <BadgeTotal
                                                :convert-negative="true"
                                                :value="value.profit_amount"
                                                color="green"
                                                symbol="$"
                                                title="Profit amount"
                                            />
                                        </td>

                                        <td>
                                            <ReverseYesNo :value="value.pcpm_negative" />
                                        </td>

                                        <td v-if="isAdditionalFieldsHidden">
                                            <span
                                                class="label bg-yellow"
                                                title="Undelivered Messages"
                                            >
                                                {{ value.messages_undelivered_percent }}%
                                            </span>

                                            <span
                                                class="label bg-yellow"
                                                title="Undelivered Messages"
                                            >
                                                {{ value.messages_undelivered }}
                                            </span>
                                        </td>

                                        <td v-if="isAdditionalFieldsHidden">
                                            <span
                                                class="label bg-red"
                                                title="Failed Messages"
                                            >
                                                {{ value.messages_failed_percent }}%
                                            </span>

                                            <span
                                                class="label bg-red"
                                                title="Failed Messages"
                                            >
                                                {{ value.messages_failed }}
                                            </span>
                                        </td>

                                        <td v-if="isAdditionalFieldsHidden">
                                            <span
                                                class="label bg-black"
                                                title="Blacklisted Messages"
                                            >
                                                {{ value.messages_blacklisted_percent }}%
                                            </span>

                                            <span
                                                class="label bg-black"
                                                title="Blacklisted Messages"
                                            >
                                                {{ value.messages_blacklisted }}
                                            </span>
                                        </td>

                                        <td v-if="isAdditionalFieldsHidden">
                                            <span
                                                class="label bg-orange"
                                                title="Unsubscribed Messages"
                                            >
                                                {{ value.messages_unsubscribed_percent }}%
                                            </span>

                                            <span
                                                class="label bg-orange"
                                                title="Unsubscribed Messages"
                                            >
                                                {{ value.messages_unsubscribed }}
                                            </span>
                                        </td>

                                        <td v-if="isAdditionalFieldsHidden">
                                            <span
                                                class="label bg-purple"
                                                title="Two Way Replied Messages"
                                            >
                                                {{ value.messages_2way_replied_percent }}%
                                            </span>

                                            <span
                                                class="label bg-purple"
                                                title="Two Way Replied Messages"
                                            >
                                                {{ value.messages_2way_replied }}
                                            </span>
                                        </td>
                                    </tr>
                                </template>
                            </tbody>

                            <tfoot>
                                <tr>
                                    <th :colspan="colspanTotal">Total</th>
                                    <th>
                                        <span title="Total">{{ report.totals.messages_count }}</span>
                                    </th>
                                    <th>
                                        <span title="Sent">{{ report.totals.messages_sent_percent }}% ({{ report.totals.messages_sent }})</span>
                                    </th>
                                    <th>{{ report.totals.clicks_percent }}% ({{ report.totals.clicks_count }})</th>
                                    <th>${{ report.totals.revenue_amount }} ({{ report.totals.revenue_count }})</th>
                                    <th>${{ report.totals.pcpm }} / ${{ report.totals.ecpm }} / ${{ report.totals.epc }}</th>
                                    <th>${{ report.totals.cost }}</th>
                                    <th>${{ report.totals.profit_amount }}</th>
                                    <th>
                                        <ReverseYesNo :value="report.totals.pcpm_negative" />
                                    </th>
                                    <th v-if="isAdditionalFieldsHidden">
                                        <span title="Undelivered"
                                            >{{ report.totals.messages_undelivered_percent }}% ({{ report.totals.messages_undelivered }})</span
                                        >
                                    </th>
                                    <th v-if="isAdditionalFieldsHidden">
                                        <span title="Failed"
                                            >{{ report.totals.messages_failed_percent }}% ({{ report.totals.messages_failed }})</span
                                        >
                                    </th>
                                    <th v-if="isAdditionalFieldsHidden">
                                        <span title="Blacklisted"
                                            >{{ report.totals.messages_blacklisted_percent }}% ({{ report.totals.messages_blacklisted }})</span
                                        >
                                    </th>
                                    <th v-if="isAdditionalFieldsHidden">
                                        <span title="Unsubscribed"
                                            >{{ report.totals.messages_unsubscribed_percent }}% ({{
                                                report.totals.messages_unsubscribed
                                            }})</span
                                        >
                                    </th>
                                    <th v-if="isAdditionalFieldsHidden">
                                        <span title="2WayReplied"
                                            >{{ report.totals.messages_2way_replied_percent }}% ({{
                                                report.totals.messages_2way_replied
                                            }})</span
                                        >
                                    </th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                <div
                    v-else
                    class="box-body clearfix"
                >
                    <p>{{ $t('messages.report.noReport') }}</p>
                </div>
            </vue-slide-up-down>
        </template>
    </div>
</template>

<script>
import EventBus from '@/event-bus';
import SortBy from '@/components/subcomponents/SortBy';
import BadgeTotal from '@/components/subcomponents/BadgeTotal';
import ReverseYesNo from '@/components/subcomponents/ReverseYesNo';

export default {
    name: 'DashboardTable',
    components: {
        SortBy,
        BadgeTotal,
        ReverseYesNo,
    },
    props: {
        isLoading: {
            type: Boolean,
            default: false,
        },
        report: {
            type: Object,
            default: () => ({}),
        },
        typeReport: {
            type: String,
            default: '',
        },
        additionalRelationTitles: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            isCollapse: false,
            sortDirections: [],
            onlyWithMessages: true,
            isAdditionalFieldsHidden: false,
            sort: {
                name: '',
                direction: '',
            },
        };
    },
    computed: {
        colspanTotal() {
            return this.additionalRelationTitles.length + 2;
        },
        isReport() {
            return !!Object.keys(this.report).length;
        },
        isReportItems() {
            return this.isReport && this.report.items && this.report.items.length;
        },
        items() {
            const items = [...((this.report && this.report.items) || [])];

            return this.sort.direction
                ? items.sort((a, b) => {
                      if (this.sort.direction === 'desc') {
                          return a[this.sort.name] - b[this.sort.name];
                      } else {
                          return b[this.sort.name] - a[this.sort.name];
                      }
                  })
                : this.report.items;
        },
        domains() {
            return this.report.domains;
        },
    },
    mounted() {
        EventBus.$on('setSort', this.setSort);
    },
    methods: {
        setSort(name) {
            if (this.sort.name !== name || !this.sort.direction) {
                this.sort.direction = 'desc';
            } else if (this.sort.name === name && this.sort.direction === 'desc') {
                this.sort.direction = 'asc';
            } else if (this.sort.name === name && this.sort.direction === 'asc') {
                this.sort.direction = '';
            }

            this.sort.name = name;
        },
        getSort(name) {
            return this.sort.name === name && this.sort.direction ? 'sort-' + this.sort.direction : 'unsorted';
        },
        filterByMessages() {
            this.onlyWithMessages = !this.onlyWithMessages;
        },
        changeAdditionalFieldsVisibility() {
            this.isAdditionalFieldsHidden = !this.isAdditionalFieldsHidden;
        },
    },
};
</script>
